import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  MenuItem,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  ListItemText,
  Alert,
  FormControlLabel,
} from "@mui/material";
import { departmentService, leaveService } from "../../services/leaveService";

interface ApplyLeaveProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface LeaveType {
  id: number;
  name: string;
  description?: string;
  requires_substitute: boolean;
}

type DutyTravelOption = "" | "no" | "general" | "circular";

interface Department {
  id: number;
  name: string;
}

const ApplyLeave: React.FC<ApplyLeaveProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    reason: "",
    substitute_id: "",
    people_to_notify: [] as string[],
    include_weekends: false,
    include_holidays: false,
  });
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [notifyEmployees, setNotifyEmployees] = useState<any[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [allowOtherDepartmentSubstitute, setAllowOtherDepartmentSubstitute] =
    useState(false);
  const [selectedSubstituteDepartmentId, setSelectedSubstituteDepartmentId] =
    useState("");
  const [currentDepartmentId, setCurrentDepartmentId] = useState<number | null>(
    null,
  );
  const [isVCApprove, setIsVCApprove] = useState(false);
  const [vcApprovePosition, setVcApprovePosition] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [numberOfDays, setNumberOfDays] = useState(0);
  const [attachmentRules, setAttachmentRules] = useState<any[]>([]);
  const [approvalRules, setApprovalRules] = useState<any[]>([]);
  const [selectedApprovalChain, setSelectedApprovalChain] = useState<any[]>([]);
  const [requiresSubstituteAcceptance, setRequiresSubstituteAcceptance] =
    useState(1);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [currentRequirement, setCurrentRequirement] = useState<any>(null);
  const [vacationPeriods, setVacationPeriods] = useState<any[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [leaveBalance, setLeaveBalance] = useState<any>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [holidayDates, setHolidayDates] = useState<string[]>([]);
  const [showDutyLeaveNotice, setShowDutyLeaveNotice] = useState(false);
  const [dutySubsistence, setDutySubsistence] = useState("");
  const [dutyTravelOption, setDutyTravelOption] =
    useState<DutyTravelOption>("");

  const isDutyLeave = Boolean(
    leaveTypes
      .find((t) => t.id === parseInt(formData.leave_type_id))
      ?.name?.toLowerCase()
      .includes("duty"),
  );

  useEffect(() => {
    loadLeaveTypes();
    loadDepartments();
    loadNotifyEmployees();
  }, []);

  useEffect(() => {
    if (allowOtherDepartmentSubstitute) {
      if (selectedSubstituteDepartmentId) {
        loadEmployees({
          includeOtherDepartments: true,
          departmentId: parseInt(selectedSubstituteDepartmentId, 10),
        });
      } else {
        setEmployees([]);
      }
      return;
    }

    loadEmployees();
  }, [allowOtherDepartmentSubstitute, selectedSubstituteDepartmentId]);

  useEffect(() => {
    calculateDays();
  }, [
    formData.start_date,
    formData.end_date,
    formData.include_weekends,
    formData.include_holidays,
    holidayDates,
  ]);

  useEffect(() => {
    const loadHolidays = async () => {
      if (!formData.start_date || !formData.end_date) {
        setHolidayDates([]);
        return;
      }

      try {
        const holidays = await leaveService.getHolidays(
          formData.start_date,
          formData.end_date,
        );
        const normalizedDates = (holidays || []).map(
          (holiday: any) => String(holiday.holiday_date).split("T")[0],
        );
        setHolidayDates(normalizedDates);
      } catch (error) {
        console.error("Failed to load holidays:", error);
        setHolidayDates([]);
      }
    };

    loadHolidays();
  }, [formData.start_date, formData.end_date]);

  useEffect(() => {
    if (numberOfDays > 0 && attachmentRules.length > 0) {
      const matching = attachmentRules.find((r) => {
        const minDays = Number(r.minDays ?? r.min_days ?? 0);
        const rawMaxDays = r.maxDays ?? r.max_days;
        const maxDays =
          rawMaxDays === "" || rawMaxDays === null || rawMaxDays === undefined
            ? null
            : Number(rawMaxDays);
        return (
          numberOfDays >= minDays &&
          (maxDays === null || Number.isNaN(maxDays) || numberOfDays <= maxDays)
        );
      });
      setCurrentRequirement(matching || null);
    } else {
      setCurrentRequirement(null);
    }
  }, [numberOfDays, attachmentRules]);

  const loadLeaveTypes = async () => {
    try {
      const types = await leaveService.getAvailableLeaveTypes();
      console.log("Loaded leave types from API:", types);
      setLeaveTypes(types);
    } catch (error) {
      console.error("Failed to load leave types:", error);
    }
  };

  const loadDepartments = async () => {
    try {
      const rows = await departmentService.getDepartments();
      if (Array.isArray(rows)) {
        setDepartments(rows.map((d: any) => ({ id: d.id, name: d.name })));
      }
    } catch (error) {
      console.error("Failed to load departments:", error);
      setDepartments([]);
    }
  };

  const loadEmployees = async (options?: {
    includeOtherDepartments?: boolean;
    departmentId?: number;
  }) => {
    try {
      const data = await leaveService.getActiveEmployees(options);
      // Handle new response shape with VCAPPROVE info
      if (data && data.employees) {
        setEmployees(data.employees);
        setIsVCApprove(data.is_vcapprove || false);
        setVcApprovePosition(data.position || null);
        if (data.current_department_id) {
          setCurrentDepartmentId(data.current_department_id);
        }
      } else if (Array.isArray(data)) {
        setEmployees(data);
      }
    } catch (error) {
      console.error("Failed to load employees:", error);
    }
  };

  // Always company-wide, independent of substitute department selection.
  const loadNotifyEmployees = async () => {
    try {
      const data = await leaveService.getActiveEmployees({ all: true });
      if (data && data.employees) {
        setNotifyEmployees(data.employees);
      } else if (Array.isArray(data)) {
        setNotifyEmployees(data);
      }
    } catch (error) {
      console.error("Failed to load notify employees:", error);
    }
  };

  const calculateDays = () => {
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const holidaySet = new Set(holidayDates);

      let days = 0;
      const current = new Date(start);

      while (current <= end) {
        const dayOfWeek = current.getDay();
        const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;
        const isHoliday = holidaySet.has(dateStr);

        if (
          (formData.include_weekends || (dayOfWeek !== 0 && dayOfWeek !== 6)) &&
          (formData.include_holidays || !isHoliday)
        ) {
          days++;
        }
        current.setDate(current.getDate() + 1);
      }

      setNumberOfDays(days);
    } else {
      setNumberOfDays(0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.leave_type_id ||
      !formData.start_date ||
      !formData.end_date ||
      !formData.reason
    ) {
      alert("Please fill in all required fields");
      return;
    }

    const selectedType = leaveTypes.find(
      (t) => t.id === parseInt(formData.leave_type_id),
    );
    if (selectedType?.requires_substitute && !formData.substitute_id) {
      alert("A substitute is required for this leave type");
      return;
    }

    if (isDutyLeave && (!dutySubsistence || !dutyTravelOption)) {
      alert(
        "Please specify whether you are requesting Substistance and the Travelling Expenditure / Allowance option",
      );
      return;
    }
    // VCAPPROVE: substitute is always mandatory
    if (isVCApprove && !formData.substitute_id) {
      alert(
        `As ${vcApprovePosition?.designation_name || "a position holder"}, you must assign a substitute before applying for leave.`,
      );
      return;
    }

    if (numberOfDays <= 0) {
      alert("End date must be after start date");
      return;
    }

    if (
      currentRequirement &&
      (currentRequirement.requirementType === "required" ||
        currentRequirement.requirement_type === "required") &&
      selectedFiles.length === 0
    ) {
      setFormError(
        currentRequirement.description ||
          "Attachments are required for this leave duration.",
      );
      return;
    }

    setSubmitting(true);

    try {
      const selectedSubstitute = employees.find(
        (emp) => emp.id.toString() === formData.substitute_id,
      );
      const isCrossDepartmentSelection =
        !!selectedSubstitute &&
        currentDepartmentId !== null &&
        selectedSubstitute.department_id !== currentDepartmentId;

      let finalReason = formData.reason;
      if (isDutyLeave) {
        const travelLabel =
          dutyTravelOption === "no"
            ? "No"
            : dutyTravelOption === "general"
              ? "General"
              : dutyTravelOption === "circular"
                ? "As per the internal circular 02/2023"
                : "";
        finalReason = `${finalReason}\n\n[Subsistence Requested: ${dutySubsistence === "yes" ? "Yes" : "No"}\nTravelling Expenditure / Allowance Requested: ${travelLabel}]`;
      }

      const submitData = new FormData();
      submitData.append("leave_type_id", formData.leave_type_id);
      submitData.append("start_date", formData.start_date);
      submitData.append("end_date", formData.end_date);
      submitData.append("reason", finalReason);
      if (formData.substitute_id) {
        submitData.append("substitute_id", formData.substitute_id);
        if (selectedSubstitute?.department_id) {
          submitData.append(
            "substitute_department_id",
            selectedSubstitute.department_id.toString(),
          );
        }
      }
      if (isCrossDepartmentSelection) {
        submitData.append("allow_cross_department", "true");
      }
      // Send people_to_notify as JSON array of IDs
      if (formData.people_to_notify.length > 0) {
        submitData.append(
          "people_to_notify",
          JSON.stringify(formData.people_to_notify.map((id) => parseInt(id))),
        );
      }

      selectedFiles.forEach((file) => {
        submitData.append("attachments", file);
      });

      if (formData.include_weekends) {
        submitData.append("include_weekends", "true");
      }
      if (formData.include_holidays) {
        submitData.append("include_holidays", "true");
      }

      await leaveService.applyForLeave(submitData);

      onSuccess();
    } catch (error: any) {
      console.error("Failed to apply for leave:", error);
      const errorMessage =
        error.response?.status === 413
          ? "Upload too large. Please keep each attachment under 10 MB and reduce total upload size."
          : error.response?.data?.error || "Failed to apply for leave";
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange =
    (field: string) =>
    async (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (field === "leave_type_id" && value) {
        const selectedType = leaveTypes.find((t) => t.id === parseInt(value));
        if (selectedType?.name?.toLowerCase().includes("duty")) {
          setShowDutyLeaveNotice(true);
        }

        try {
          const res = await leaveService.getLeaveRequirements(parseInt(value));
          setAttachmentRules(res.attachment_rules || []);
          setApprovalRules(res.approval_rules || []);
          setRequiresSubstituteAcceptance(
            res.requires_substitute_acceptance || 1,
          );
          setVacationPeriods(res.vacation_periods || []);
        } catch (error) {
          console.error("Failed to fetch requirements:", error);
        }

        // Fetch leave balance for selected type
        try {
          setBalanceLoading(true);
          const balances = await leaveService.getLeaveBalance();
          const selectedBalance = balances.find(
            (b: any) => b.leave_type_id === parseInt(value),
          );
          setLeaveBalance(selectedBalance || null);
        } catch (error) {
          console.error("Failed to fetch leave balance:", error);
          setLeaveBalance(null);
        } finally {
          setBalanceLoading(false);
        }
      } else if (field === "leave_type_id" && !value) {
        setLeaveBalance(null);
      }
    };

  const handleAllowOtherDepartmentChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    setAllowOtherDepartmentSubstitute(checked);
    setSelectedSubstituteDepartmentId("");
    setFormData((prev) => ({
      ...prev,
      substitute_id: "",
    }));
  };

  const handleSubstituteDepartmentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = event.target.value;
    setSelectedSubstituteDepartmentId(value);
    setFormData((prev) => ({
      ...prev,
      substitute_id: "",
    }));
  };

  const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/bmp",
    "image/tiff",
    "application/pdf",
  ];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const invalid = files.filter((f) => !ALLOWED_FILE_TYPES.includes(f.type));
      if (invalid.length > 0) {
        alert(
          `Only images and PDF files are allowed. Rejected: ${invalid.map((f) => f.name).join(", ")}`,
        );
        e.target.value = "";
        return;
      }
      const tooLarge = files.filter((f) => f.size > MAX_FILE_SIZE);
      if (tooLarge.length > 0) {
        alert(
          `Each file must be under 10 MB. Too large: ${tooLarge.map((f) => f.name).join(", ")}`,
        );
        e.target.value = "";
        return;
      }
      setSelectedFiles(files);
    }
  };

  const getMinEndDate = () => {
    return formData.start_date || new Date().toISOString().split("T")[0];
  };

  return (
    <Dialog open maxWidth="md" fullWidth onClose={onClose}>
      <Dialog
        open={showDutyLeaveNotice}
        onClose={() => setShowDutyLeaveNotice(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duty Leave - Please Note</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            When applying for Duty Leave, please clearly mention the following:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            1. Whether you are requesting <strong>Substistance</strong>.
          </Typography>
          <Typography variant="body2">
            2. <strong>Travelling Expenditure / Allowance</strong> - select{" "}
            <strong>No</strong>, <strong>General</strong>, or{" "}
            <strong>As per the internal circular 02/2023</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDutyLeaveNotice(false)}
            variant="contained"
          >
            OK, Understood
          </Button>
        </DialogActions>
      </Dialog>
      <DialogTitle>Apply for Leave</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {/* Display form error */}
          {formError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setFormError(null)}
            >
              {formError}
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Leave Type *"
                value={formData.leave_type_id}
                onChange={handleChange("leave_type_id")}
                required
              >
                <MenuItem value="">
                  <em>Select Leave Type</em>
                </MenuItem>
                {leaveTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id.toString()}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Display vacation period restrictions if applicable */}
            {vacationPeriods.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Vacation Period Restriction
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This leave type can only be applied within the following
                    period(s):
                  </Typography>
                  {vacationPeriods.map((vp, idx) => (
                    <Box key={idx} sx={{ ml: 2, mb: 0.5 }}>
                      <Typography variant="body2">
                        <strong>{vp.name}:</strong>{" "}
                        {new Date(vp.start_date).toLocaleDateString()} -{" "}
                        {new Date(vp.end_date).toLocaleDateString()}
                        {vp.description && ` (${vp.description})`}
                      </Typography>
                    </Box>
                  ))}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date *"
                type="date"
                value={formData.start_date}
                onChange={handleChange("start_date")}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: new Date().toISOString().split("T")[0],
                }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date *"
                type="date"
                value={formData.end_date}
                onChange={handleChange("end_date")}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: getMinEndDate(),
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_weekends}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        include_weekends: e.target.checked,
                      }))
                    }
                  />
                }
                label="Include weekends (Saturdays & Sundays) in day calculation"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_holidays}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        include_holidays: e.target.checked,
                      }))
                    }
                  />
                }
                label="Include public holidays in day calculation"
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body1" component="div">
                  <strong>Number of Days: {numberOfDays}</strong>
                  {(formData.include_weekends || formData.include_holidays) && (
                    <Typography
                      variant="caption"
                      color="info.main"
                      sx={{ ml: 1 }}
                    >
                      (
                      {[
                        formData.include_weekends ? "including weekends" : null,
                        formData.include_holidays ? "including holidays" : null,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      )
                    </Typography>
                  )}
                </Typography>
                {numberOfDays > 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    From {formData.start_date} to {formData.end_date}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Leave Balance Info */}
            {leaveBalance && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor:
                      leaveBalance.available_days < numberOfDays &&
                      numberOfDays > 0
                        ? "error.main"
                        : "success.main",
                    bgcolor:
                      leaveBalance.available_days < numberOfDays &&
                      numberOfDays > 0
                        ? "#ffebee"
                        : "#e8f5e9",
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Leave Balance — {leaveBalance.leave_type}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Typography variant="body2">
                      <strong>Entitled:</strong> {leaveBalance.total_days} days
                    </Typography>
                    <Typography variant="body2">
                      <strong>Used:</strong> {leaveBalance.used_days} days
                    </Typography>
                    <Typography variant="body2">
                      <strong>Pending:</strong> {leaveBalance.pending_days || 0}{" "}
                      days
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      <strong>Available:</strong> {leaveBalance.available_days}{" "}
                      days
                    </Typography>
                  </Box>
                  {numberOfDays > 0 &&
                    leaveBalance.available_days < numberOfDays && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        Insufficient balance! You have{" "}
                        {leaveBalance.available_days} day(s) available but are
                        requesting {numberOfDays} day(s).
                      </Alert>
                    )}
                </Box>
              </Grid>
            )}
            {balanceLoading && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="textSecondary">
                    Loading leave balance...
                  </Typography>
                </Box>
              </Grid>
            )}

            {isDutyLeave && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Duty Leave Details
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Requesting Substistance? *"
                    value={dutySubsistence}
                    onChange={(e) => setDutySubsistence(e.target.value)}
                    sx={{ mb: 2, bgcolor: "background.paper" }}
                    required
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Travelling Expenditure / Allowance *"
                    value={dutyTravelOption}
                    onChange={(e) =>
                      setDutyTravelOption(e.target.value as DutyTravelOption)
                    }
                    sx={{ bgcolor: "background.paper" }}
                    required
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="circular">
                      As per the internal circular 02/2023
                    </MenuItem>
                  </TextField>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason/Remarks *"
                value={formData.reason}
                onChange={handleChange("reason")}
                required
                placeholder="Please provide a detailed reason for your leave application..."
              />
            </Grid>

            <Grid item xs={12}>
              {isVCApprove && vcApprovePosition && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Position-Based Requirement
                  </Typography>
                  <Typography variant="body2">
                    As <strong>{vcApprovePosition.designation_name}</strong> (
                    {vcApprovePosition.department_name}), you must assign a
                    substitute who will act in your position during your
                    absence. Your leave will be approved by the Vice Chancellor.
                  </Typography>
                </Alert>
              )}

              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    checked={allowOtherDepartmentSubstitute}
                    onChange={handleAllowOtherDepartmentChange}
                  />
                }
                label="Choose substitute from another department"
              />

              {allowOtherDepartmentSubstitute && (
                <TextField
                  select
                  fullWidth
                  sx={{ mb: 2 }}
                  label="Select Substitute Department *"
                  value={selectedSubstituteDepartmentId}
                  onChange={handleSubstituteDepartmentChange}
                  helperText="Select a department to load substitute candidates"
                >
                  <MenuItem value="">
                    <em>Select Department</em>
                  </MenuItem>
                  {departments
                    .filter(
                      (dept) =>
                        currentDepartmentId === null ||
                        dept.id !== currentDepartmentId,
                    )
                    .map((dept) => (
                      <MenuItem key={dept.id} value={dept.id.toString()}>
                        {dept.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              <TextField
                select
                fullWidth
                label={
                  isVCApprove
                    ? "Select Acting Substitute *"
                    : leaveTypes.find(
                          (t) => t.id === parseInt(formData.leave_type_id),
                        )?.requires_substitute
                      ? "Select Substitute *"
                      : "Select Substitute (Optional)"
                }
                value={formData.substitute_id}
                onChange={handleChange("substitute_id")}
                disabled={
                  !isVCApprove &&
                  allowOtherDepartmentSubstitute &&
                  !selectedSubstituteDepartmentId
                }
                required={
                  isVCApprove ||
                  leaveTypes.find(
                    (t) => t.id === parseInt(formData.leave_type_id),
                  )?.requires_substitute
                }
                helperText={
                  isVCApprove
                    ? `Mandatory: Select an employee to act as ${vcApprovePosition?.designation_name || "your position"} during your absence`
                    : allowOtherDepartmentSubstitute
                      ? "Cross-department substitute must accept before approval workflow starts. Substitute's HOD will be notified."
                      : leaveTypes.find(
                            (t) => t.id === parseInt(formData.leave_type_id),
                          )?.requires_substitute
                        ? "A substitute is mandatory for this leave type"
                        : "Pick an employee who will handle your responsibilities while you are on leave"
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id.toString()}>
                    {emp.name} ({emp.designation || emp.designation_name})
                    {emp.department_name ? ` - ${emp.department_name}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {currentRequirement && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#fff3e0",
                    borderRadius: 1,
                    border: "1px solid #ffe0b2",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="warning.main"
                    gutterBottom
                  >
                    Attachment Required:{" "}
                    {(
                      currentRequirement.requirementType ||
                      currentRequirement.requirement_type ||
                      "OPTIONAL"
                    ).toUpperCase()}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {currentRequirement.description ||
                      "Please upload supporting documents for your leave application."}
                  </Typography>
                  <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                    Upload Documents
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                    />
                  </Button>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Accepted: images and PDF files (max 10 MB each)
                  </Typography>
                  {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        Selected files:
                      </Typography>
                      {selectedFiles.map((f, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          sx={{ display: "block", ml: 1 }}
                        >
                          - {f.name} ({(f.size / 1024).toFixed(1)} KB)
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {(currentRequirement.requirementType === "required" ||
                    currentRequirement.requirement_type === "required") &&
                    selectedFiles.length === 0 && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 1 }}
                      >
                        * At least one document is required
                      </Typography>
                    )}
                </Box>
              </Grid>
            )}

            {/* Always show optional attachment upload when no specific requirement */}
            {!currentRequirement && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Attachments (Optional)
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    You can upload supporting documents for your leave
                    application (e.g., medical certificates, travel documents).
                  </Typography>
                  <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                    Upload Documents
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                    />
                  </Button>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Accepted: images and PDF files (max 10 MB each)
                  </Typography>
                  {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        Selected files:
                      </Typography>
                      {selectedFiles.map((f, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          sx={{ display: "block", ml: 1 }}
                        >
                          - {f.name} ({(f.size / 1024).toFixed(1)} KB)
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Autocomplete
                multiple
                fullWidth
                options={notifyEmployees}
                value={notifyEmployees.filter((employee) =>
                  formData.people_to_notify.includes(String(employee.id)),
                )}
                onChange={(_event, selectedEmployees) => {
                  setFormData((prev) => ({
                    ...prev,
                    people_to_notify: selectedEmployees.map((employee) =>
                      String(employee.id),
                    ),
                  }));
                }}
                getOptionLabel={(employee) =>
                  `${employee.name} (${employee.designation || "Employee"})`
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                disableCloseOnSelect
                limitTags={2}
                renderOption={(props, employee, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} />
                    <ListItemText
                      primary={`${employee.name} (${employee.designation || "Employee"})`}
                    />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Notify Other People"
                    placeholder="Search by name or designation"
                    helperText="Search and select individuals who should be informed about this leave application"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={loading} variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              submitting ||
              numberOfDays === 0 ||
              (leaveBalance != null &&
                leaveBalance.available_days < numberOfDays)
            }
            sx={{ minWidth: 120 }}
          >
            {submitting ? <CircularProgress size={24} /> : "Apply for Leave"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ApplyLeave;
